import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBigFiveSet } from '../api/client';
import type { BigFiveSet } from '../api/types';
import { AdSlot } from '../components/AdSlot';
import { AXIS_LABELS, BIG_FIVE_DOMAIN_NAMES } from '../diagnosis/labels';

/**
 * ビッグファイブ版の説明（`/help/bigfive`。doc/ASSESSMENT.md §9）。
 *
 * 入口の「別の測り方で」に出る「ビッグファイブ」が何を測っているか、結果ページの 5 因子の読み方、
 * そして型とキャラへの「無理やりの当てはめ」をどうやっているかを、根拠と限界ごと書く。
 *
 * 書き方の方針は `/help` `/help/jung` と同じ。断定せず、できないことはできないと書く。
 * 因子・側面の名前と説明文は bigfive.json（`fetchBigFiveSet`）から読み、結果ページと二重に持たない。
 * 「ビッグファイブ」「5 因子モデル」は学術上の名前なので使える。商標名（BFI-2 など特定の質問紙の名前）は出典として挙げるだけで、
 * この診断の名称には使わない。測定精度が未検証であること（doc/ASSESSMENT.md §0）はここでも省かない。
 */

/** 4 二分法と 5 因子の対応（McCrae & Costa, 1989 の相関）。写像（§9.3）の一次の根拠。 */
const CORRESPONDENCE: readonly { readonly axis: string; readonly domain: string; readonly r: string; readonly note: string }[] = [
  { axis: 'EI', domain: 'E', r: '.74', note: '外向性が高いほど外向（E）' },
  { axis: 'SN', domain: 'O', r: '.72', note: '開放性が高いほど直観（N）' },
  { axis: 'TF', domain: 'A', r: '.44', note: '協調性が高いほど感情（F）。対応はやや弱い' },
  { axis: 'JP', domain: 'C', r: '.49', note: '誠実性が高いほど判断（J）。開放性が高いと知覚（P）寄り（r ≈ −.30）' },
  { axis: 'AY', domain: 'E', r: '—', note: '外向性の「自己主張性」が高いほど主張型（A）。協調性の「敬意」が高いと受容型（Y）寄り' },
  { axis: 'CV', domain: 'N', r: '—', note: '神経症傾向が低いほど泰然型（C）、高いほど繊細型（V）' },
];

export function BigFiveHelpPage() {
  const [set, setSet] = useState<BigFiveSet | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void fetchBigFiveSet().then((value) => { if (!cancelled) setSet(value); }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);
  const axisLabel = (axis: string) => AXIS_LABELS.find((a) => a.axis === axis);

  return (
    <main className="page help">
      <p className="page__kicker">ヘルプ</p>
      <h1 className="page__title">ビッグファイブについて</h1>
      <p className="page__lead">
        入口の「別の測り方で」にある<strong>ビッグファイブ</strong>は、「さっと」「ふつう」「じっくり」とは別の質問紙（60 問）です。ここでは、ビッグファイブが何なのか、この診断でどう測っているか、結果ページの数字の読み方、そしてキャラクターと型にどう当てはめているか（その限界も）を説明します。よくある質問は<Link to="/help">ヘルプ</Link>にあります。
      </p>

      <nav className="help__toc" aria-label="目次">
        <a className="chip" href="#theory">ビッグファイブとは</a>
        <a className="chip" href="#method">この診断での測り方</a>
        <a className="chip" href="#domains">5 因子と 15 側面</a>
        <a className="chip" href="#reading">結果の読み方</a>
        <a className="chip" href="#mapping">型・キャラへの当てはめ</a>
        <a className="chip" href="#limits">気をつけてほしいこと</a>
      </nav>

      <section className="section" id="theory">
        <h2 className="section__title">ビッグファイブとは</h2>
        <div className="help__a">
          <p>
            人の性格の個人差を、互いにほぼ独立した <strong>5 つの因子</strong>の組み合わせでとらえる考え方です。性格を表す言葉（形容詞）を大量に集めて、人々の自己評価・他者評価がどうまとまるかを統計（因子分析）で調べる研究が 1930〜80 年代に積み重なり、言語や文化を変えても、おおむね 5 つの因子が繰り返し見つかりました（Goldberg, 1990。Costa & McCrae の NEO 尺度もこの 5 因子を測ります）。
          </p>
          <p>
            5 つの因子は、<strong>外向性</strong>（Extraversion）、<strong>協調性</strong>（Agreeableness）、<strong>誠実性</strong>（Conscientiousness）、
            <strong>神経症傾向</strong>（Neuroticism。近年の質問紙では Negative Emotionality とも）、<strong>開放性</strong>（Openness）です。頭文字で OCEAN とも呼ばれます。日本語の呼び方は研究者によって少し違い、誠実性を「勤勉性」、神経症傾向を「情緒不安定性」と書くこともあります。
          </p>
          <p>
            ビッグファイブは<strong>型ではありません</strong>。人を「〜型」に分けるのではなく、5 つの因子それぞれを連続したものさしで測り、「外向性はやや高め、誠実性は中程度…」というふうに<strong>プロフィール</strong>として読みます。仕事の成果、学業、健康、人間関係との関連が多くの研究で調べられており、現代の性格心理学で最も裏付けの多い枠組みです。一方で、5 因子で性格のすべてが説明できるわけではなく、因子の数（6 因子の HEXACO など）や側面の切り方には今も議論があります。
          </p>
        </div>
      </section>

      <section className="section" id="method">
        <h2 className="section__title">この診断での測り方</h2>
        <div className="help__a">
          <p>
            <strong>60 問</strong>で、5 因子 × 3 側面 × 4 問。側面ごとに正項目 2 問・逆転項目 2 問です。この分け方（因子・側面・項目数・正逆の釣り合い）は、現在もっとも使われている質問紙のひとつ BFI-2（Soto & John, 2017。日本語版もあります）と同じにしています。ただし<strong>項目文は GenDan が書いたもの</strong>で、他の質問紙の文をそのまま使ってはいません。書き方の流儀は「じっくり」などと同じで、性格を評価する言い方（「私は社交的だ」）ではなく<strong>行動を訊き</strong>（「週末は人と会う予定を入れる」）、1 文に 1 つの内容だけを入れ、どちらの答えも肯定的に読めるようにしています。
          </p>
          <p>
            採点はサーバ側で行います。5 段階の回答を −2〜+2 に写し、逆転項目は符号を反転して、側面は 4 問の平均、因子は 3 側面（12 問）の平均を 0〜100 に直します（50 が「どちらともいえない」）。因子ごとに<strong>測定の標準誤差</strong>（SEM ＝ 標準偏差 × √(1 − 信頼性)）を出し、結果ページではスコアの周りに帯として描きます。
          </p>
          <p>
            信頼性（内的整合性）は、受けた人の回答が集まったら実データで算出します（<code>tools/bigfive-psychometrics.mjs</code>）。それまでは BFI-2 の報告値に合わせた暫定値（因子 0.85、側面 0.70）で誤差の幅を計算し、結果ページに「暫定」と明示します。受けた人が 300 人を超えたあとで基準を作り直すと（同じスクリプトが基準のファイルを書き出し、次のデプロイで入ります）、スコアを「これまでに受けた人の中での位置（百分位）」に切り替えます。300 人を超えても、基準を作り直すまでは自動では切り替わりません。
          </p>
          <p>
            他の 3 つの深さと違い、<strong>追い設問はありません</strong>（ビッグファイブは型を決めるものではないので、拮抗を割る必要がありません）。ログインして受けておくと、ビッグファイブどうしでは回答を使い回せます。3 つの深さとのあいだでは設問が違うので使い回せません。
          </p>
        </div>
      </section>

      <section className="section" id="domains">
        <h2 className="section__title">5 因子と 15 側面</h2>
        <div className="help__a">
          {failed && <p className="section__note">因子と側面の一覧を読み込めませんでした。時間をおいてもう一度開いてください。</p>}
          {!set && !failed && <p className="section__note">読み込んでいます…</p>}
          {set && (
            <>
              <p>{set.description}</p>
              <table className="help__table">
                <thead>
                  <tr><th>因子</th><th>測っていること</th><th>高いと</th><th>低いと</th></tr>
                </thead>
                <tbody>
                  {set.domains.map((d) => (
                    <tr key={d.id}>
                      <td><strong>{d.name}</strong><br /><span className="help__nowrap">{d.en}</span></td>
                      <td>{d.measures}</td>
                      <td>{d.high}</td>
                      <td>{d.low}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>それぞれの因子は、次の 3 つの側面から測ります。側面は因子より短い尺度（4 問）なので、誤差の幅は因子より広くなります。</p>
              <table className="help__table">
                <thead>
                  <tr><th>因子</th><th>側面</th><th>高い側</th><th>低い側</th></tr>
                </thead>
                <tbody>
                  {set.facets.map((f) => (
                    <tr key={f.id}>
                      <td className="help__nowrap">{BIG_FIVE_DOMAIN_NAMES[f.domain] ?? f.domain}</td>
                      <td><strong>{f.name}</strong><br /><span className="help__nowrap">{f.en}</span></td>
                      <td>{f.high}</td>
                      <td>{f.low}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </section>

      <section className="section" id="reading">
        <h2 className="section__title">結果の読み方</h2>
        <div className="help__a">
          <p>
            結果ページの「どう測ったか」には、5 因子それぞれのスコア（0〜100）と、その周りの<strong>帯</strong>が出ます。帯は「±1 標準誤差」で、本当の値がその幅に入る見込みがおよそ 68% という意味です。
            2 つの因子の帯が重なっているときは、どちらが高いかを確かに言うことはできません。
          </p>
          <p>
            「高い」「やや高い」「中程度」「やや低い」「低い」の言葉は、基準ができるまでは目盛りの中央（50）から見た言い方です。人の中での位置ではないので、たとえば協調性や誠実性は多くの人が中央より高めに答える傾向があり、「やや高い」が特別に高いとは限りません。百分位に切り替わったあとは、上位 16%（±1 標準偏差の外）を「高い」、上位 31% までを「やや高い」のように読みます。
          </p>
          <p>
            <strong>高い・低いに良い悪いはありません。</strong>たとえば神経症傾向が高いことは、危険や問題に早く気づく感度の高さでもあります。協調性が低いことは、率直に物を言い、必要な対立を避けない強さでもあります。結果の文はどちらの側も肯定的に読めるように書いています。
          </p>
          <p>
            「15 の側面ごとのスコアを見る」を開くと、因子の中でどの側面が高く、どの側面が低いかが分かります。同じ「外向性が中程度」でも、社交性は高く自己主張性は低い、というように中身は人によって違います。因子より側面のほうが、その人らしさをよく表すことがあります。
          </p>
        </div>
      </section>

      <section className="section" id="mapping">
        <h2 className="section__title">型・キャラへの当てはめ</h2>
        <div className="help__a">
          <p>
            この診断のキャラクター（テーマの登場人物）は、6 本の軸・18 の側面の「向き」で選ぶ仕組みになっています。ビッグファイブは型ではないので、キャラを出すには<strong>5 因子を 6 本の軸に写す</strong>必要があります。これは<strong>無理やりの当てはめ</strong>で、結果ページでもそう書いています。ただし写し方は勝手に決めたものではなく、次の実証研究の対応に沿っています。
          </p>
          <p>
            McCrae と Costa（1989）は、4 文字の型の元になっている 4 つの二分法を連続スコアとして測り、5 因子との相関を報告しました。外向／内向は外向性と（r ≈ .74）、感覚／直観は開放性と（.72）、思考／感情は協調性と（.44）、判断／知覚は誠実性と（.49）強く対応し、
            4 つの二分法はおおむね 5 因子のうち 4 つを別の言葉で測っている、というのがその結論です。神経症傾向に対応する二分法は 4 文字の型には無く、この診断の副軸「泰然型／繊細型」がそれにあたります。
          </p>
          <table className="help__table">
            <thead>
              <tr><th>この診断の軸</th><th>主に写す因子</th><th>報告された相関</th><th>写し方</th></tr>
            </thead>
            <tbody>
              {CORRESPONDENCE.map((row) => {
                const axis = axisLabel(row.axis);
                return (
                  <tr key={row.axis}>
                    <td className="help__nowrap"><span className="typecode">{axis?.low.code}／{axis?.high.code}</span><br />{axis?.caption}</td>
                    <td className="help__nowrap">{BIG_FIVE_DOMAIN_NAMES[row.domain]}</td>
                    <td className="help__nowrap">{row.r}</td>
                    <td>{row.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p>
            実際の写し方は側面の単位で行います。たとえば「社交性」（軸 EI の側面）は外向性の社交性をそのまま、「対立の扱い」（軸 TF の側面）は協調性の敬意を主に、思いやりと自己主張性（負）を少し混ぜて作ります。
            18 の側面それぞれの重み（合計 1）は <code>packages/scoring/src/bigfive.ts</code> にあり、技術資料 doc/ASSESSMENT.md §9.3 に一覧があります。写した 18 側面から、他の深さと同じ手順で型（軸ごとに 50 を境に文字を決める）とキャラ（向きがいちばん似ている相手）を出します。
          </p>
          <p>
            <strong>限界。</strong>相関 .44〜.74 は「かなり対応する」水準ですが、同じものではありません。特に思考／感情（協調性との r ≈ .44）は対応が弱く、ビッグファイブで受けた人の T／F は、「じっくり」で軸そのものを測ったときと違う文字になることが珍しくありません。だから結果ページでは、5 因子のスコアを主結果として先に出し、型とキャラは「目安」として後ろに置いています。型やキャラをきちんと知りたいときは「じっくり」（72 問）で軸そのものを測ってください。同じ人が両方を受けたデータが集まったら、写した軸と測った軸の一致率を算出して、写し方の重みを見直します。
          </p>
        </div>
      </section>

      <section className="section" id="limits">
        <h2 className="section__title">気をつけてほしいこと</h2>
        <div className="help__a">
          <p>
            <strong>自己申告です。</strong>その日の気分や、答えるときに思い浮かべた場面（仕事か、家か）でスコアは動きます。
            2〜4 週間あけて「同じ深さでもう一度」を受けると前回との比較が出るので、どのくらい安定しているかを自分で確かめられます。
          </p>
          <p>
            <strong>測定精度は未検証です。</strong>項目はビッグファイブの確立した構造に沿って設計していますが、この項目文での信頼性・妥当性の数値はまだ実測できていません。集まった回答で算出し、弱い項目があれば差し替えて版を上げます。基準（受けた人の分布）ができるまでは、スコアは目盛りの中央から見た素点です。
          </p>
          <p>
            <strong>専門家の検査ではありません。</strong>「神経症傾向」は研究で使われている因子名で、病気や診断名ではありません。気分の落ち込みや不安がつらいときは、この結果ではなく医療機関や相談窓口に頼ってください。採用や選抜の判断に使えるだけの精度は確かめられていません。
          </p>
          <p>
            <strong>参考。</strong>
            Goldberg, L. R. (1990). An alternative "description of personality": The Big-Five factor structure. <em>JPSP</em>.
            McCrae, R. R., &amp; Costa, P. T. (1989). Reinterpreting the Myers-Briggs Type Indicator from the perspective of the five-factor model of personality. <em>Journal of Personality</em>.
            Soto, C. J., &amp; John, O. P. (2017). The next Big Five Inventory (BFI-2). <em>JPSP</em>.
            吉野伸哉・下司忠大・小塩真司ほか (2022). 日本語版 BFI-2 の作成. <em>心理学研究</em>.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="page__actions">
          <Link className="button button--primary" to="/help">ヘルプに戻る</Link>
          <Link className="button button--ghost" to="/help/jung">ユングのタイプ論について</Link>
          <Link className="button button--ghost" to="/">トップへ戻る</Link>
          <Link className="button button--ghost" to="/contact">お問い合わせ</Link>
        </div>
      </section>
      <AdSlot placement="help" />
    </main>
  );
}
